import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Swal from "sweetalert2";
import {
  Stack,
  Button,
  Container,
  Grid,
  Input,
  InputAdornment,
  Accordion,
  AccordionSummary,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { debounce } from "../../../utils/debounce";
import config from "../../../config";
import i18n from "../../../i18n";
import { convertMessageCodeToMessage } from "../../../utils/messageCodeToMessage";
import Iconify from "../../../components/iconify/Iconify";
import {
  deleteCertificateTypes,
  getCertificateTypesList,
} from "../../../services/training.certificate.service";
import CertificateTypesTable from "../../../SharedComponent/CertificateTypesTable";

const TABLE_HEAD = [
  {
    id: "type",
    label: i18n.t("type"),
    alignRight: false,
    sortable: true,
  },
  { id: "" },
];

class TypeOfCertificates extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      typeOfTrainingCertificates: [],
      typeOfTrainingCertificate: {},
      page: 1,
      search: "",
      limit: 100,
      skip: 0,
      total_typeOfTrainingCertificates: 0,
      expanded: false,
      tableLoader: false,
      pageLoader: false,
      orderBy: { sort_key: "type", sort_order: true },
      deleteTypeOfCertificatesPopup: false,
    };
    this.useStyles = {
      sticky: {
        position: "sticky",
        left: "0",
        zIndex: "5",
      },
    };
  }

  async componentDidMount() {
    await this.getCertificateTypesData();
  }

  async setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }

  async getCertificateTypesData() {
    this.setState({
      tableLoader: true,
    });

    let query = {
      limit: this.state.limit,
      skip: this.state.skip,
      sort_key: this.state?.orderBy?.sort_key,
      sort_order: this.state?.orderBy?.sort_order ? "ASC" : "DESC",
    };

    if (this.state.search) {
      query.search = this.state.search;
    }

    let typeOfTrainingCertificates = await getCertificateTypesList(query);
    if (typeOfTrainingCertificates.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: typeOfTrainingCertificates?.error?.error_code
          ? i18n.t(
              convertMessageCodeToMessage(
                typeOfTrainingCertificates?.error_code
              )
            )
          : typeOfTrainingCertificates?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      this.setState({
        tableLoader: false,
      });
      return;
    }

    this.setState({
      tableLoader: false,
      typeOfTrainingCertificates: typeOfTrainingCertificates.training_certificate_types,
      total_typeOfTrainingCertificates:typeOfTrainingCertificates?.count
    });
  };

  async handleDeleteCertificateTypes(trainingCertificateId) {
    this.setState({
      pageLoader: true,
    });
    let response = await deleteCertificateTypes(trainingCertificateId);

    if (response && response.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: response?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(response?.error_code))
          : response?.error,
      });
      return;
    }

    if (response) {
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: i18n.t("success"),
        text: response?.message_code
          ? i18n.t(convertMessageCodeToMessage(response?.message_code))
          : response?.message,
        showConfirmButton: false,
        timer: config.toastDelay,
        timerProgressBar: true,
      });
      await this.getCertificateTypesData();
      this.setState({
        pageLoader: false,
      });
    }
  };

  handleOpenMenu = (event) => {
    this.setState({ open: event.currentTarget });
  };

  handleCloseMenu = () => {
    this.setState({ open: null });
  };

  getCertificateTypesDataOnchange = debounce(() => {
    this.getCertificateTypesData("filters");
  }, config.searchDebounceDelay);

  getCertificateTypesDataOnchangeWithoutDebounce = () => {
    this.getCertificateTypesData("filters");
  };

  handleSortAction = async (key) => {
    await this.setState({
      orderBy: {
        sort_key: key,
        sort_order: !this.state.orderBy?.sort_order,
      },
    });
    this.getCertificateTypesData();
  };

  handleActionMenu = (data, rowData) => {
    this.setState({
      typeOfTrainingCertificate: rowData,
    });
    this.handleOpenMenu(data);
  };

  handleDeleteTypeOfCertificate = async () => {
    this.handleCloseMenu();
    await this.setState({
      deleteTypeOfCertificatesPopup: true,
    });
  };

  handlePagination=async(page)=>{
    await this.setState({
      skip: (page - 1) * this.state.limit,
      page
    });
    this.getCertificateTypesData();
  }

  handleLimitChange=async(limit)=>{
    await this.setState({
      limit,
      skip:0
    });
    this.getCertificateTypesData();
  }

  render() {
    return (
      <Container maxWidth="xxl" disableGutters>
        <Helmet>
          <title>
            {" "}
            {i18n.t("typeOfTrainingCertificate")} | {config.APPLICATION_NAME}{" "}
          </title>
        </Helmet>
        <Grid fullWidth>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <div style={{ width: "100%", marginRight: "10px" }}>
              <Accordion
                TransitionProps={{ unmountOnExit: true }}
                expanded={this.state.expanded}
                sx={{
                  background: "white",
                }}
              >
                <AccordionSummary
                  style={{ background: "white" }}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{ margin: 0 }}
                >
                  <Input
                    autoFocus
                    fullWidth
                    disableUnderline
                    placeholder="Search…"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "text.disabled", width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    }
                    onChange={(e) => {
                      this.setState({
                        search: e?.target?.value,
                      });
                      this.getCertificateTypesDataOnchange();
                    }}
                    sx={{ mr: 1, fontWeight: "fontWeightBold" }}
                  />
                </AccordionSummary>
              </Accordion>
            </div>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              className="BtnWidth"
              onClick={() => {
                this.props.navigate("/settings/type_of_certificates/add");
              }}
            >
              {i18n.t("training_certificate")}
            </Button>
          </Stack>
        </Grid>

        <CertificateTypesTable
          data={this.state}
          handleSortAction={this.handleSortAction}
          handleOpenMenu={this.handleOpenMenu}
          handleCloseMenu={this.handleCloseMenu}
          handleActionMenu={this.handleActionMenu}
          handleDeleteTypeOfCertificate={this.handleDeleteTypeOfCertificate}
          handlePagination={this.handlePagination}
          handleLimitChange={this.handleLimitChange}
          tableHeaders={TABLE_HEAD}
        />

        <Dialog
          open={this.state?.deleteTypeOfCertificatesPopup}
          onClose={() => {
            this.setState({
              deleteTypeOfCertificatesPopup: false,
            });
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{i18n.t("delete")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {i18n.t("are_you_sure_of_deleting_this_action_is_irreversible")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                this.setState({
                  deleteTypeOfCertificatesPopup: false,
                });
              }}
              autoFocus
            >
              {i18n.t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                this.setState({
                  deleteTypeOfCertificatesPopup: false,
                });
                this.handleDeleteCertificateTypes(
                  this.state.typeOfTrainingCertificate._id
                );
              }}
            >
              {i18n.t("proceed")}
            </Button>
          </DialogActions>
        </Dialog>

        <Backdrop
          sx={{
            color: "#e5e5e5",
            zIndex: (theme) => theme.zIndex.drawer + 1000,
          }}
          open={this.state?.pageLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }
}

// Wrap and export
export default function (props) {
  const navigate = useNavigate();
  const params = useParams();

  return <TypeOfCertificates {...props} navigate={navigate} params={params} />;
}
